import React, { useLayoutEffect, useRef, useState } from 'react';
import { Product, ProductImageDetails } from '@inter-ikea-storageone/range';
import Swiper, { SwiperItem } from './Swiper';
import ImageThumbnails, { Thumbnail } from './ImageThumbnails';
import ProductImage from 'components/ProductImage/ProductImage';
import AdditionalItems from 'ui/AdditionalItemsButton/AdditionalItemsButton';
import { ProductDetailsProps } from 'ducks/layout';
import { getMPPImage } from 'products';

interface ImageSwiperProps {
  images: ProductImageDetails[];
  currentImageId: string;
  onSwipe: (id: string) => void;
  onAnimationEnd?: () => void;
}

const SwiperGallery = ({ images, currentImageId, onSwipe }: ImageSwiperProps) => {
  const ref = useRef<HTMLDivElement>(null);

  const image = images.find(img => img.id === currentImageId);

  if (!image) {
    return null;
  }

  return (
    <Swiper
      ref={ref}
      currentIndex={images.indexOf(image)}
      onSwipe={direction => {
        const newIndex = (images.indexOf(image) + direction + images.length) % images.length;
        onSwipe(images[newIndex].id);
      }}
    >
      {images.map(image => (
        <SwiperItem key={image.id}>
          <ProductImage sizes="15rem" image={image} alt="Product image" />
        </SwiperItem>
      ))}
    </Swiper>
  );
};

const SwiperImages = ({
  image,
  onSelect,
  selected
}: {
  image: ProductImageDetails & { alt: string };
  onSelect: () => void;
  selected: boolean;
}) => {
  return (
    <Thumbnail tabIndex={selected ? -1 : 0} aria-selected={selected} onSelect={onSelect}>
      <ProductImage sizes="6rem" image={image} alt={image.alt} />
    </Thumbnail>
  );
};

type Props = {
  product?: Product;
  productDetails: ProductDetailsProps | null;
};

const ImageSwiper = ({ product, productDetails }: Props) => {
  const [showThumbnails, setShowThumbnails] = useState<boolean>(false);
  const [currentImageId, setCurrentImageId] = useState<string>('0');

  const productId = productDetails?.productId;
  const images = product?.images || [];
  const thumbnails = images.slice(0, 5);
  const remainingThumbnails = images.slice(5);
  const additionalItems = images.length - thumbnails.length;

  useLayoutEffect(() => {
    if (product) {
      const mpp = getMPPImage(product);

      if (mpp) {
        setCurrentImageId(mpp.id);
      }
    }
  }, [product]);

  const showAllThumbnailImages = () => {
    setShowThumbnails(true);
  };

  return (
    <>
      <SwiperGallery
        images={images}
        currentImageId={currentImageId}
        onSwipe={newId => setCurrentImageId(newId)}
      />
      {images.length > 1 && (
        <ImageThumbnails>
          {thumbnails.map((image, index) => (
            <SwiperImages
              key={`${productId}-thumb-${image.id}`}
              selected={image.id === currentImageId}
              image={{ ...image, alt: `Product image thumbnail ${index + 1}` }}
              onSelect={() => setCurrentImageId(image.id)}
            />
          ))}
          {showThumbnails &&
            remainingThumbnails.map((image, index) => (
              <SwiperImages
                key={`${productId}-thumb-${image.id}`}
                selected={image.id === currentImageId}
                image={{ ...image, alt: `Product image thumbnail ${index + 1}` }}
                onSelect={() => setCurrentImageId(image.id)}
              />
            ))}
          {!showThumbnails && additionalItems > 0 && (
            <AdditionalItems
              data-testid={`additional-button`}
              onClick={() => showAllThumbnailImages()}
              numberOfItems={additionalItems}
            />
          )}
        </ImageThumbnails>
      )}
    </>
  );
};

export default ImageSwiper;
